import { api, type InfoShareSummary, type SharedFolderSummary } from '../api/client';
import { sedrApi, type SedrRoomSummary } from '../api/sedr';
import { resolveFolderShareLabel, resolveInfoShareLabel } from '../crypto/shareLabelUpload';
import type { IconName } from '../ui/icons';
import { isPendingSedrDeletionApproval } from './sedr/sedrDeletionPending';

export type HomeActionKind =
  | 'info-unread'
  | 'folder-new'
  | 'folder-updated'
  | 'room-new'
  | 'room-updated'
  | 'room-deletion';

export type HomeRecentKind = 'folder' | 'room';

export interface HomeActionItem {
  id: string;
  kind: HomeActionKind;
  href: string;
  icon: IconName;
  title: string;
  reasonKey: string;
  reasonParams?: Record<string, unknown>;
  sortAt: number;
}

export interface HomeRecentItem {
  id: string;
  kind: HomeRecentKind;
  href: string;
  icon: IconName;
  title: string;
  lastViewedAt: number;
}

export interface HomeDashboardData {
  outstanding: HomeActionItem[];
  recent: HomeRecentItem[];
}

const OUTSTANDING_CAP = 8;
const RECENT_CAP = 6;

function parseTime(value: string | null | undefined): number {
  if (!value) return 0;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
}

function isFolderOutstanding(folder: SharedFolderSummary): HomeActionKind | null {
  if (folder.displayStatus !== 'Active') return null;
  if (!folder.lastViewedAt) return 'folder-new';
  const viewed = parseTime(folder.lastViewedAt);
  const updated = parseTime(folder.updatedAt);
  if (updated > viewed) return 'folder-updated';
  return null;
}

function isRoomOutstanding(room: SedrRoomSummary, currentUserId: string): HomeActionKind | null {
  if (isPendingSedrDeletionApproval(room, currentUserId)) {
    return 'room-deletion';
  }
  if (room.archivedAt) return null;
  if (room.lifecycleState !== 'Active' && room.lifecycleState !== 'SetupIncomplete') {
    return null;
  }
  if (!room.lastViewedAt) return 'room-new';
  const viewed = parseTime(room.lastViewedAt);
  const modified = parseTime(room.lastModifiedAt);
  if (modified > viewed) return 'room-updated';
  return null;
}

async function folderTitle(folder: SharedFolderSummary): Promise<string> {
  if (!folder.labelBlobId) return '';
  try {
    return (await resolveFolderShareLabel(folder.id, folder.wrappedFolderKey, folder.labelBlobId)) ?? '';
  } catch {
    return '';
  }
}

async function shareTitle(share: InfoShareSummary): Promise<string> {
  if (!share.labelBlobId) return '';
  try {
    return (await resolveInfoShareLabel(share.id, share.labelBlobId)) ?? '';
  } catch {
    return '';
  }
}

function kindPriority(kind: HomeActionKind): number {
  switch (kind) {
    case 'room-deletion':
      return 0;
    case 'info-unread':
      return 1;
    case 'folder-new':
    case 'room-new':
      return 2;
    case 'folder-updated':
    case 'room-updated':
      return 3;
    default:
      return 9;
  }
}

export async function loadHomeDashboardData(currentUserId: string): Promise<HomeDashboardData> {
  const [sharesResult, foldersResult, roomsResult] = await Promise.allSettled([
    api.listInfoShares(),
    api.listSharedFolders(),
    sedrApi.listRooms(),
  ]);

  const shares = sharesResult.status === 'fulfilled' ? sharesResult.value : [];
  const folders = foldersResult.status === 'fulfilled' ? foldersResult.value : [];
  const rooms = roomsResult.status === 'fulfilled' ? roomsResult.value : [];

  const outstanding: HomeActionItem[] = [];

  for (const share of shares) {
    if (share.unreadReplyCount <= 0) continue;
    const title = await shareTitle(share);
    outstanding.push({
      id: `info:${share.id}`,
      kind: 'info-unread',
      href: '/information',
      icon: 'info-share',
      title,
      reasonKey: 'home.outstanding.unreadReplies',
      reasonParams: { count: share.unreadReplyCount },
      sortAt: parseTime(share.createdAt),
    });
  }

  for (const folder of folders) {
    const kind = isFolderOutstanding(folder);
    if (!kind) continue;
    const title = await folderTitle(folder);
    outstanding.push({
      id: `folder:${folder.id}`,
      kind,
      href: `/files/${folder.id}`,
      icon: 'files',
      title,
      reasonKey: kind === 'folder-new' ? 'home.outstanding.newAccess' : 'home.outstanding.updated',
      sortAt: parseTime(folder.updatedAt) || parseTime(folder.createdAt),
    });
  }

  for (const room of rooms) {
    const kind = isRoomOutstanding(room, currentUserId);
    if (!kind) continue;
    outstanding.push({
      id: `room:${room.id}`,
      kind,
      href: `/sedr/${room.id}`,
      icon: 'sedr',
      title: room.title,
      reasonKey:
        kind === 'room-deletion'
          ? 'home.outstanding.approveDeletion'
          : kind === 'room-new'
            ? 'home.outstanding.newAccess'
            : 'home.outstanding.updated',
      sortAt: parseTime(room.lastModifiedAt) || parseTime(room.createdAt),
    });
  }

  outstanding.sort((a, b) => {
    const byKind = kindPriority(a.kind) - kindPriority(b.kind);
    if (byKind !== 0) return byKind;
    return b.sortAt - a.sortAt;
  });

  const outstandingIds = new Set(outstanding.map((item) => item.id));
  const recent: HomeRecentItem[] = [];

  for (const folder of folders) {
    const id = `folder:${folder.id}`;
    if (!folder.lastViewedAt || outstandingIds.has(id) || folder.displayStatus !== 'Active') continue;
    const title = await folderTitle(folder);
    recent.push({
      id,
      kind: 'folder',
      href: `/files/${folder.id}`,
      icon: 'files',
      title,
      lastViewedAt: parseTime(folder.lastViewedAt),
    });
  }

  for (const room of rooms) {
    const id = `room:${room.id}`;
    if (!room.lastViewedAt || outstandingIds.has(id) || room.archivedAt) continue;
    recent.push({
      id,
      kind: 'room',
      href: `/sedr/${room.id}`,
      icon: 'sedr',
      title: room.title,
      lastViewedAt: parseTime(room.lastViewedAt),
    });
  }

  recent.sort((a, b) => b.lastViewedAt - a.lastViewedAt);

  return {
    outstanding: outstanding.slice(0, OUTSTANDING_CAP),
    recent: recent.slice(0, RECENT_CAP),
  };
}
